import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button,
  Alert, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, CircularProgress, Stack,
} from '@mui/material';
import { Refresh as RefreshIcon, Add as AddIcon } from '@mui/icons-material';
import { aiMappingApi, patternTaxonomyApi } from '../../services/api';
import SuggestionChip from './SuggestionChip';
import TypePicker from './TypePicker';
import SampleProducts from './SampleProducts';
import MappingImpact, { impactSentence } from './MappingImpact';
import BulkMapBar, { RowCheckbox, SelectAllCheckbox, canMapToSuggestion } from './BulkMapBar';

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function UnmappedPatternsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapDialogRecord, setMapDialogRecord] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: pageSize, offset: page * pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      const data = await aiMappingApi.getUnmappedPatterns(params);
      setRows(data.unmapped_patterns || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  // The dialog maps to an existing pattern type only (closed list); the AI suggestion is preselected.
  const handleOpenMap = (row) => {
    setMapDialogRecord(row);
    setSelectedType(row.suggested_normalized_value || null);
    setMapDialogOpen(true);
    setTypesLoading(true);
    patternTaxonomyApi.listTypes()
      .then(data => setTypeOptions((data.types || []).map(t => ({ value: t.normalized_pattern, family: t.family }))))
      .catch(() => setTypeOptions([]))
      .finally(() => setTypesLoading(false));
  };

  const handleCreateMapping = async () => {
    if (!mapDialogRecord || !selectedType) return;
    setActionLoading(true);
    try {
      await aiMappingApi.createPatternMapping(mapDialogRecord.extracted_pattern, selectedType);
      setSuccess(`Mapped "${mapDialogRecord.extracted_pattern}" → "${selectedType}"`);
      setMapDialogOpen(false);
      setMapDialogRecord(null);
      setSelectedType(null);
      load();
    } catch (e) {
      setError(e.message || 'Failed to create mapping');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Search extracted pattern..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              sx={{ minWidth: 250 }}
            />
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Pattern labels the photo AI produced that have no mapping yet. Confident AI suggestions are
            mapped automatically every night; the labels left here need a human. Mapping picks from the
            closed list of pattern types — a new type is added on the “Pattern → Family” tab. Tick rows
            to accept several AI suggestions at once.
          </Alert>

          <BulkMapBar
            rows={rows}
            selected={selected}
            setSelected={setSelected}
            labelOf={(r) => r.extracted_pattern}
            suggestionOf={(r) => r.suggested_normalized_value}
            mapOne={(r) => aiMappingApi.createPatternMapping(r.extracted_pattern, r.suggested_normalized_value)}
            onDone={load}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"><SelectAllCheckbox rows={rows} selected={selected} setSelected={setSelected} canSelect={canMapToSuggestion} /></TableCell>
                  <TableCell>Extracted Pattern</TableCell>
                  <TableCell>AI suggestion</TableCell>
                  <TableCell>Products</TableCell>
                  <TableCell><Tooltip title="Products without a pattern yet / products carrying the label; below: what the rest hold today"><span>Will change</span></Tooltip></TableCell>
                  <TableCell align="right">Occurrences</TableCell>
                  <TableCell>Last seen</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No unmapped patterns found.</TableCell></TableRow>
                ) : (
                  rows.map(row => (
                    <TableRow key={row.id} hover selected={selected.has(row.id)}>
                      <TableCell padding="checkbox"><RowCheckbox row={row} selected={selected} setSelected={setSelected} canSelect={canMapToSuggestion} /></TableCell>
                      <TableCell><Typography variant="body2" fontWeight="medium">{row.extracted_pattern}</Typography></TableCell>
                      <TableCell><SuggestionChip row={row} /></TableCell>
                      <TableCell><SampleProducts products={row.sample_products} /></TableCell>
                      <TableCell><MappingImpact products={row.products} kind="pattern" /></TableCell>
                      <TableCell align="right">
                        <Chip label={row.occurrence_count} size="small" color={row.occurrence_count >= 10 ? 'warning' : 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell>{row.last_seen_at ? new Date(row.last_seen_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell align="right">
                        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => handleOpenMap(row)}>Map</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </CardContent>
      </Card>

      <Dialog open={mapDialogOpen} onClose={() => setMapDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Map Pattern</DialogTitle>
        <DialogContent>
          {mapDialogRecord && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle2">
                  Extracted: <Chip label={mapDialogRecord.extracted_pattern} size="small" color="primary" />
                </Typography>
                <SampleProducts products={mapDialogRecord.sample_products} size={48} />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {impactSentence(mapDialogRecord.products, 'pattern')}. Tonight's pass applies the mapping; a product whose photo's top label is another pattern keeps that one.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                Seen {mapDialogRecord.occurrence_count} time(s)
                {mapDialogRecord.suggested_normalized_value && (
                  <> · AI suggests <strong>{mapDialogRecord.suggested_normalized_value}</strong> ({Math.round((mapDialogRecord.confidence || 0) * 100)}%)</>
                )}
              </Typography>
              {(mapDialogRecord.suggestions || []).length > 1 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    The AI gave different answers — pick one, or choose another type below:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {mapDialogRecord.suggestions.map(a => (
                      <Chip
                        key={a.value}
                        label={`${a.value} · ${Math.round((a.confidence || 0) * 100)}%`}
                        size="small"
                        color={selectedType === a.value ? 'primary' : 'default'}
                        variant={selectedType === a.value ? 'filled' : 'outlined'}
                        onClick={() => setSelectedType(a.value)}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              <TypePicker
                options={typeOptions}
                value={selectedType}
                onChange={setSelectedType}
                label="Pattern type"
                loading={typesLoading}
                typesTabName="Pattern → Family"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleCreateMapping} variant="contained" disabled={!selectedType || actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Map'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
